'use client';

import classNames from 'classnames';
import { FC, MouseEvent, useCallback, useRef, useState } from 'react';

import { PropsWithClassname } from '@/app/types';
import { useTranslation } from 'react-i18next';

export interface CopyInputProps extends PropsWithClassname {
  value: string | number;
  onClick?: () => void;
}

const CopyInput: FC<CopyInputProps> = ({ value, className, onClick }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [displaySuccess, setDisplaySuccess] = useState(false);
  const { t } = useTranslation();
  const handleInputClick = useCallback((event: MouseEvent<HTMLInputElement>) => {
    (event.target as any).select?.();
  }, []);

  const handleCopyButtonClick = useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.select();
    inputRef.current.setSelectionRange(0, 99999);

    navigator.clipboard.writeText(inputRef.current.value);
    setDisplaySuccess(true);
    inputRef.current.setSelectionRange(0, 0);
    let timeout = setTimeout(() => {
      setDisplaySuccess(false);
    }, 3000);

    onClick?.();

    return () => {
      clearTimeout(timeout);
    };
  }, [onClick]);

  return (
    <div className={classNames(className, 'relative mb-[32px]')}>
      <input
        ref={inputRef}
        value={value}
        onClick={handleInputClick}
        readOnly
        className={classNames(
          className,
          'w-full border rounded text-neutral-dark px-[24px] py-[12px] outline-0',
          displaySuccess ? 'border-success-mid' : 'border-neutral-light',
        )}
      />
      <button
        onClick={handleCopyButtonClick}
        className={classNames(
          'absolute uppercase tracking-[4px] text-[12px] right-[1px] top-[1px] h-[calc(100%-2px)]  bg-[rgba(255,255,255,0)] pl-[48px] pr-[16px] rounded bg-gradient-to-l from-[rgba(255,255,255,1)] via-[rgba(255,255,255,1)] via-80%',
          displaySuccess ? 'bg-gradient-to-l text-success-mid ' : 'bg-gradient-to-l text-primary-mid ',
        )}
      >
        {displaySuccess
          ? t('components.ThankYouPageShareStep.copySuccess')
          : t('components.ThankYouPageShareStep.copy')}
      </button>
    </div>
  );
};

export default CopyInput;
